import { useMemo } from "react";
import { getObjectIds } from "modules/utils/Functions";
import { formatQuery, RuleGroupType } from "react-querybuilder";
import useInternalAdminUserAttributes from "./use-internal-admin-user-attributes";
import useInternalAdminUserAttributeCategories from "./use-internal-admin-user-attribute-categories";

interface IProps {
  query?: RuleGroupType;
}

const useInternalAdminDistributionGroupQuery = ({ query }: IProps) => {
  // we don't know if the ids in the query are userAttributes or userAttribute categories so we need to query for both
  const unparsedQuery = useMemo(() => {
    if (!query) return null;
    return formatQuery(query, "jsonata");
  }, [query]);

  const objectIds = getObjectIds(unparsedQuery);

  const { userAttributes } = useInternalAdminUserAttributes(objectIds);
  const { userAttributeCategories } =
    useInternalAdminUserAttributeCategories(objectIds);

  const naturalLanguageQuery = useMemo(() => {
    if (!unparsedQuery) return null;
    let _query = unparsedQuery;
    userAttributes.forEach((userAttribute) => {
      _query = _query.replaceAll(userAttribute.id, userAttribute.name);
    });
    userAttributeCategories.forEach((userAttributeCategory) => {
      _query = _query.replaceAll(
        userAttributeCategory.id,
        userAttributeCategory.name,
      );
    });
    return _query;
  }, [unparsedQuery, userAttributeCategories, userAttributes]);

  const mongoDbQuery = useMemo(() => {
    if (!query) return null;
    return JSON.stringify(formatQuery(query, "mongodb_query"));
  }, [query]);

  const serializedQuery = useMemo(() => {
    if (!query) return null;
    return JSON.stringify(query);
  }, [query]);

  return {
    naturalLanguageQuery,
    serializedQuery,
    mongoDbQuery,
  };
};

export default useInternalAdminDistributionGroupQuery;
